/**
 * Performance Monitoring Utilities
 * Comprehensive web vitals and performance tracking
 */

export interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, unknown>;
}

export interface WebVitalsMetric extends PerformanceMetric {
  id: string;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric[]> = new Map();
  private observers: PerformanceObserver[] = [];
  private webVitalsCallbacks: ((metric: WebVitalsMetric) => void)[] = [];

  constructor() {
    this.initializeObservers();
  }

  /**
   * Initialize performance observers for various metrics
   */
  private initializeObservers(): void {
    // Observe Core Web Vitals
    if ('PerformanceObserver' in window) {
      try {
        // LCP (Largest Contentful Paint)
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as PerformanceEntry;

          this.recordWebVital({
            name: 'LCP',
            value: lastEntry.startTime,
            id: 'v3-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            rating: this.getRating('LCP', lastEntry.startTime),
            delta: lastEntry.startTime,
            timestamp: Date.now(),
          });
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(lcpObserver);

        // FID (First Input Delay)
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: PerformanceEventTiming) => {
            this.recordWebVital({
              name: 'FID',
              value: entry.processingStart - entry.startTime,
              id: 'v3-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
              rating: this.getRating('FID', entry.processingStart - entry.startTime),
              delta: entry.processingStart - entry.startTime,
              timestamp: Date.now(),
            });
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.push(fidObserver);

        // CLS (Cumulative Layout Shift)
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0;
          const entries = list.getEntries() as (PerformanceEntry & { value: number; hadRecentInput?: boolean })[];

          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });

          this.recordWebVital({
            name: 'CLS',
            value: clsValue,
            id: 'v3-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            rating: this.getRating('CLS', clsValue),
            delta: clsValue,
            timestamp: Date.now(),
          });
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
        this.observers.push(clsObserver);

        // Navigation timing
        const navigationObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries() as PerformanceNavigationTiming[];
          entries.forEach((entry) => {
            this.recordMetric('navigation-timing', {
              name: 'navigation-timing',
              value: entry.duration,
              timestamp: Date.now(),
              metadata: {
                domContentLoaded: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
                loadComplete: entry.loadEventEnd - entry.loadEventStart,
                responseTime: entry.responseEnd - entry.requestStart,
              },
            });
          });
        });
        navigationObserver.observe({ entryTypes: ['navigation'] });
        this.observers.push(navigationObserver);

        // Resource timing for bundle analysis
        const resourceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries() as PerformanceResourceTiming[];
          entries.forEach((entry) => {
            if (entry.initiatorType === 'script' || entry.initiatorType === 'link') {
              this.recordMetric('resource-timing', {
                name: entry.name,
                value: entry.duration,
                timestamp: Date.now(),
                metadata: {
                  type: entry.initiatorType,
                  size: entry.transferSize,
                  cached: entry.transferSize === 0,
                },
              });
            }
          });
        });
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.push(resourceObserver);

      } catch (error) {
        console.warn('Performance observer not supported:', error);
      }
    }
  }

  /**
   * Get rating for Core Web Vitals based on thresholds
   */
  private getRating(metric: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    switch (metric) {
      case 'LCP':
        return value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
      case 'FID':
        return value <= 100 ? 'good' : value <= 300 ? 'needs-improvement' : 'poor';
      case 'CLS':
        return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
      default:
        return 'good';
    }
  }

  /**
   * Record a Web Vital metric
   */
  private recordWebVital(metric: WebVitalsMetric): void {
    this.webVitalsCallbacks.forEach(callback => callback(metric));
    this.recordMetric(metric.name, metric);
  }

  /**
   * Record a custom performance metric
   */
  public recordMetric(name: string, metric: PerformanceMetric): void {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    this.metrics.get(name)!.push(metric);

    // Keep only last 100 metrics per type
    const metrics = this.metrics.get(name)!;
    if (metrics.length > 100) {
      metrics.shift();
    }
  }

  /**
   * Subscribe to Web Vitals updates
   */
  public onWebVitals(callback: (metric: WebVitalsMetric) => void): () => void {
    this.webVitalsCallbacks.push(callback);
    return () => {
      const index = this.webVitalsCallbacks.indexOf(callback);
      if (index > -1) {
        this.webVitalsCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Get all metrics for a specific type
   */
  public getMetrics(name: string): PerformanceMetric[] {
    return this.metrics.get(name) || [];
  }

  /**
   * Get the latest metric for a specific type
   */
  public getLatestMetric(name: string): PerformanceMetric | null {
    const metrics = this.metrics.get(name);
    return metrics && metrics.length > 0 ? metrics[metrics.length - 1] : null;
  }

  /**
   * Get performance summary
   */
  public getSummary(): Record<string, unknown> {
    const summary: Record<string, unknown> = {};

    for (const [name, metrics] of this.metrics.entries()) {
      if (metrics.length > 0) {
        const values = metrics.map(m => m.value);
        summary[name] = {
          count: metrics.length,
          latest: metrics[metrics.length - 1],
          average: values.reduce((a, b) => a + b, 0) / values.length,
          min: Math.min(...values),
          max: Math.max(...values),
        };
      }
    }

    return summary;
  }

  /**
   * Clear all metrics
   */
  public clear(): void {
    this.metrics.clear();
  }

  /**
   * Destroy the monitor and clean up observers
   */
  public destroy(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.webVitalsCallbacks = [];
    this.metrics.clear();
  }
}

// Singleton instance
let performanceMonitor: PerformanceMonitor | null = null;

export const getPerformanceMonitor = (): PerformanceMonitor => {
  if (!performanceMonitor) {
    performanceMonitor = new PerformanceMonitor();
  }
  return performanceMonitor;
};

/**
 * React hook for performance monitoring
 */
export const usePerformanceMonitor = () => {
  return getPerformanceMonitor();
};

/**
 * React hook for Web Vitals tracking
 */
export const useWebVitals = (callback?: (metric: WebVitalsMetric) => void) => {
  const monitor = usePerformanceMonitor();

  React.useEffect(() => {
    if (callback) {
      return monitor.onWebVitals(callback);
    }
  }, [callback, monitor]);

  return monitor;
};

/**
 * Utility function to measure function execution time
 */
export const measureExecutionTime = <T>(
  fn: () => T,
  name: string,
  metadata?: Record<string, unknown>
): T => {
  const start = performance.now();
  const result = fn();
  const duration = performance.now() - start;

  getPerformanceMonitor().recordMetric('function-execution', {
    name,
    value: duration,
    timestamp: Date.now(),
    metadata,
  });

  return result;
};

/**
 * React hook for measuring component render time
 */
export const useRenderTime = (componentName: string) => {
  const renderStartRef = React.useRef<number>();
  const monitor = usePerformanceMonitor();

  React.useLayoutEffect(() => {
    renderStartRef.current = performance.now();
  });

  React.useEffect(() => {
    if (renderStartRef.current) {
      const renderTime = performance.now() - renderStartRef.current;
      monitor.recordMetric('component-render', {
        name: `${componentName}-render`,
        value: renderTime,
        timestamp: Date.now(),
      });
    }
  });
};

/**
 * Bundle size analysis utility
 */
export const analyzeBundleSize = async (): Promise<void> => {
  // This would integrate with vite-bundle-analyzer
  console.log('Bundle analysis requires vite-bundle-analyzer plugin');
};

// Re-export React for the hook
import React from 'react';